import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/shared/utils/cn';

export interface BreadcrumbItem {
  label: ReactNode;
  to?: string;
  current?: boolean;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  'aria-label'?: string;
  className?: string;
}

export function Breadcrumb({ items, className, 'aria-label': ariaLabel }: BreadcrumbProps) {
  const visibleItems = items.filter((i) => i.label != null);
  if (visibleItems.length === 0) return null;

  return (
    <nav
      aria-label={ariaLabel ?? 'Breadcrumb'}
      className={cn('flex items-center gap-2 text-xs text-fg-muted', className)}
    >
      {visibleItems.map((item, index) => {
        const isLast = index === visibleItems.length - 1;
        const isCurrent = Boolean(item.current) || isLast;

        return (
          <span key={index} className="inline-flex items-center gap-2">
            {item.to && !isCurrent ? (
              <Link
                to={item.to}
                className="font-medium text-fg-muted underline-offset-2 hover:text-primary hover:underline"
              >
                {item.label}
              </Link>
            ) : (
              <span className={isCurrent ? 'font-medium text-primary' : undefined}>
                {item.label}
              </span>
            )}

            {!isLast ? (
              <span aria-hidden="true" className="text-fg-subtle">
                /
              </span>
            ) : null}
          </span>
        );
      })}
    </nav>
  );
}
